import BaseApi from './BaseApi.js';

class DashboardApi extends BaseApi {
  // ============================================================================
  // ORGANIZATION DASHBOARD METHODS (for breadcrumbs)
  // ============================================================================

  /**
   * Get organization dashboard data including userMenu
   * @param {string} orgUnique - Organization unique identifier
   * @returns {Promise<Object>} Dashboard data with userMenu
   */
  static async getOrganizationDashboard(orgUnique) {
    const endpoint = `/org/${orgUnique}/dashboard`;
    return await this.get(endpoint);
  }

  /**
   * Get user menu for current organization
   * @param {string} orgUnique - Organization unique identifier
   * @returns {Promise<Array>} User menu items
   */
  static async getUserMenu(orgUnique) {
    const response = await this.getOrganizationDashboard(orgUnique);

    // Return the whole data object, let the action extract userMenu
    return response.data;
  }

  // ============================================================================
  // USER DASHBOARD METHODS (for user dashboard page)
  // ============================================================================

  /**
   * Fetch user profile data
   * @returns {Promise<Object>} User profile data
   */
  static async getUserProfile() {
    return await this.apiRequest('/user/profile', { method: 'GET' });
  }

  /**
   * Fetch user's organizations
   * @param {boolean} activeOnly - Filter for active organizations only
   * @returns {Promise<Array>} User's organizations
   */
  static async getUserOrganizations(activeOnly = false) {
    return await this.apiRequest(`/user/organizations?activeOnly=${activeOnly}`, { method: 'GET' });
  }

  /**
   * Fetch user's upcoming events/agenda
   * @param {number} limit - Maximum number of events to return
   * @returns {Promise<Array>} User's agenda
   */
  static async getUserAgenda(limit = 10) {
    return await this.apiRequest(`/user/agenda?limit=${limit}`, { method: 'GET' });
  }

  /**
   * Fetch user's notifications
   * @returns {Promise<Array>} User's notifications
   */
  static async getUserNotifications() {
    return await this.apiRequest('/user/notifications', { method: 'GET' });
  }

  /**
   * Mark notification as read
   * @param {number} notificationId - Notification ID to mark as read
   * @returns {Promise<Object>} Response from API
   */
  static async markNotificationAsRead(notificationId) {
    // User dashboard notifications are user-level, not organization-specific
    return await this.apiRequest(`/user/notifications/${notificationId}/read`, { method: 'PUT' });
  }

  /**
   * Get calendar timezones
   * @returns {Promise<Object>} Calendar timezones data
   */
  static async getCalendarTimezones() {
    return await this.apiRequest('/calendar/timezones', { method: 'GET' });
  }

  /**
   * Reject invitation from organization
   * @param {string} organizationUnique - Organization unique identifier
   * @returns {Promise<Object>} Response from API
   */
  static async rejectInvitation(organizationUnique) {
    return await this.apiRequest(`/user/rejectInvitation/${organizationUnique}`, { method: 'POST' });
  }
}

export default DashboardApi;
